import React, { useState, useEffect } from "react";
import { FormInput, Eye, Share2, Trash2 } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import { Table, TableColumn } from "../../components/common/tables/Table";
import { TableContainer } from "../../components/common/tables/TableContainer";
import {
  IconWithTextCell,
  LinkCell,
  TextCell,
  DateCell,
  ActionsCell,
} from "../../components/common/tables/cells";
import {
  CreateShareLinkModal,
  CreateShareLinkParams,
} from "../../components/ShareLinks/CreateShareLinkModal";
import { useOrg } from "../../contexts/OrgContext.tsx";
import type { Case } from "../../types";
import type { FormSubmissionListItemDto } from "@secure-caseworker/shared";

import { useToast } from "../../contexts/ToastContext.tsx";

interface SubmissionsListProps {}

export const SubmissionsList: React.FC<SubmissionsListProps> = () => {
  const { setAlert } = useAlert();
  const { showToast } = useToast();
  const [submissions, setSubmissions] = useState<FormSubmissionListItemDto[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>("all");
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    useState<FormSubmissionListItemDto | null>(null);
  const { orgId, navigateOrg, navigateOrgUrl } = useOrg();

  useEffect(() => {
    if (orgId) {
      loadCasesAndSubmissions();
    }
  }, [orgId, selectedCaseId]);

  const loadCasesAndSubmissions = async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      // Load all cases for this org (for filter dropdown)
      const casesList = await apiClient.listCases(orgId);
      setCases(casesList);

      // Load submissions (filtered by case if selected)
      const caseIdParam = selectedCaseId === "all" ? undefined : selectedCaseId;
      const submissionsData = await apiClient.listSubmissions(
        orgId,
        caseIdParam
      );

      setSubmissions(submissionsData);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to load data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubmission = (submissionId: string) => {
    navigateOrg(`/form-submissions/${submissionId}`);
  };

  const handleShareSubmission = (submission: FormSubmissionListItemDto) => {
    setSelectedSubmission(submission);
    setShowShareModal(true);
  };

  const handleCreateShareLink = async (params: CreateShareLinkParams) => {
    if (!selectedSubmission) return { publicUrl: "", id: "" };

    const result = await apiClient.createShareLink({
      resourceType: "form_submission",
      resourceId: selectedSubmission.id,
      ...params,
    });

    return {
      publicUrl: result.publicUrl,
      id: result.id,
    };
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!confirm("Are you sure you want to delete this submission?")) return;

    try {
      await apiClient.deleteFormSubmission(submissionId);
      showToast("Submission deleted successfully");
      loadCasesAndSubmissions();
    } catch (error) {
      showToast("Failed to delete submission", "error");
    }
  };

  const columns: TableColumn<FormSubmissionListItemDto>[] = [
    {
      header: "Form Template",
      accessor: "formTemplateTitle",
      render: (value: string, row: FormSubmissionListItemDto) => (
        <IconWithTextCell
          icon={FormInput}
          iconColor="purple"
          primaryText={value}
          primaryLink={navigateOrgUrl(`/form-submissions/${row.id}`)}
          secondaryText={row.caseTitle}
        />
      ),
    },
    {
      header: "Case",
      accessor: "caseTitle",
      render: (value: string, row: FormSubmissionListItemDto) => (
        <LinkCell
          to={navigateOrgUrl(`/cases/${row.caseId}`)}
          text={value}
          fallback="Unknown Case"
        />
      ),
    },
    {
      header: "Submitted By",
      accessor: "submittedBy",
      render: (value: string | null) => (
        <TextCell>{value || "Anonymous"}</TextCell>
      ),
    },
    {
      header: "Submitted",
      accessor: "submittedAt",
      render: (value: string) => <DateCell date={value} />,
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_: string, row: FormSubmissionListItemDto) => (
        <ActionsCell
          items={[
            {
              label: "View Submission",
              icon: <Eye className="w-4 h-4" />,
              onClick: () => handleViewSubmission(row.id),
            },
            {
              label: "Share",
              icon: <Share2 className="w-4 h-4" />,
              onClick: () => handleShareSubmission(row),
            },
            {
              label: "",
              separator: true,
              onClick: () => {},
            },
            {
              label: "Delete",
              icon: <Trash2 className="w-4 h-4" />,
              onClick: () => handleDeleteSubmission(row.id),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 flex items-center gap-3 text-title-md2 font-semibold text-black dark:text-white">
            <FormInput size={32} />
            Form Submissions
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            View and manage all form submissions across cases
          </p>
        </div>
      </div>

      {/* Case Filter */}
      <div className="flex items-center gap-3">
        <label
          htmlFor="case-filter"
          className="text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Filter by case:
        </label>
        <select
          id="case-filter"
          value={selectedCaseId}
          onChange={(e) => setSelectedCaseId(e.target.value)}
          className="h-11 w-64 rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        >
          <option value="all">All Cases</option>
          {cases.map((caseItem) => (
            <option key={caseItem.id} value={caseItem.id}>
              {caseItem.title}
            </option>
          ))}
        </select>
      </div>

      {/* Submissions Table */}
      <TableContainer
        loading={loading}
        loadingText="Loading submissions..."
        isEmpty={submissions.length === 0}
        emptyIcon={<FormInput size={48} />}
        emptyTitle={`No submissions found${
          selectedCaseId !== "all" ? " for this case" : ""
        }.`}
        title="All Submissions"
        titleIcon={<FormInput size={20} />}
        count={submissions.length}
      >
        <Table columns={columns} data={submissions} />
      </TableContainer>

      {/* Share Link Modal */}
      <CreateShareLinkModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSelectedSubmission(null);
        }}
        onCreate={handleCreateShareLink}
        resourceType="form_submission"
        onViewAllLinks={() => {
          if (selectedSubmission) {
            navigateOrg(`/form-submissions/${selectedSubmission.id}`);
          }
        }}
      />
    </div>
  );
};
